import { ThunkDispatch } from "core-integration/src/store";
import { bootstrap } from "core-integration/src/store/bootstrap/thunks";
import {
  getPlayerData,
  getPlayerUpdateError,
  getRegisterError,
} from "core-integration/src/store/player/reducers";
import {
  playerUpdate,
  register,
} from "core-integration/src/store/player/thunks";
import { getRegions } from "core-integration/src/store/regions/reducers";
import { fetchRegions } from "core-integration/src/store/regions/thunks";
import range from "lodash/range";
import sortBy from "lodash/sortBy";
import values from "lodash/values";
import moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import { Trans, withTranslation, WithTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { Box } from "rebass/styled-components";
import styled from "styled-components";
import { IPlayer } from "../../types";
import Alert from "../Alert";
import Button from "../Button";
import Copy from "../Copy";
import {
  CheckboxField,
  FieldFeedback,
  InputField,
  SelectField,
} from "../FieldRenderers";
import { Main, Wrapper } from "../Layout";
import Title from "../Title";

const DOBFieldset = styled.fieldset`
  margin: 0;
  padding: 0;
  border: 0;
`;

const DOBFieldsetInner = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-top: ${({ theme }) => theme.space[2]};
    margin-left: -${({ theme }) => theme.space[2]};
    margin-right: -${({ theme }) => theme.space[2]};
  }
`;

const DOBLegend = styled.legend`
  display: block;
  padding: 0;
  margin-bottom: ${({ theme }) => theme.space[1]};
  color: ${({ theme }) => theme.colors.white};
  font-size: 1.5rem;
  font-weight: bold;
  cursor: pointer;
`;

const DOBCol = styled.div`
  margin-top: ${({ theme }) => theme.space[2]};

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    width: calc(100% / 3);
    margin-top: 0;
    padding-left: ${({ theme }) => theme.space[2]};
    padding-right: ${({ theme }) => theme.space[2]};
  }
`;

type Props = WithTranslation;

interface IState {
  birthDay: number;
  birthMonth: number;
  birthYear: number;
  email: string;
  emailAllsvenskan: boolean;
  emailFavourite: boolean;
  firstName: string;
  lastName: string;
  password: string;
  region: number;
  errorDob: string;
  errorEmail: string;
  errorFName: string;
  errorLName: string;
  errorPass: string;
  errorRegion: string;
}

const Register = ({ t }: Props) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const navigate = useNavigate();
  const player = useSelector(getPlayerData) as IPlayer | null;
  const regions = useSelector(getRegions);
  const registerError = useSelector(getRegisterError);
  const updateError = useSelector(getPlayerUpdateError);

  const doBootstrap = () => dispatch(bootstrap());
  const updatePlayer = (data: any) => dispatch(playerUpdate(data));

  const errorsTxt: { [key: string]: string } = {
    EDob: t("register.errors.dob", "</p> not valid"),
    EEmail: t("register.errors.email", "An email is required"),
    EFName: t("register.errors.fname", "First name is required"),
    ELName: t("register.errors.lname", "Last name is required"),
    EPass: t("register.errors.pass", "A password is required"),
    EPassLenght: t(
      "register.errors.passLength",
      "Password too short, minimum 6 characters",
    ),
    ERegion: t("register.errors.region", "A region is required"),
    EValidDob: t("register.errors.validDob", "A correct </p> is required"),
    EvalidEmail: t(
      "register.errors.validEmail",
      "This Email address is already registered",
    ),
    EValidPass: t("register.errors.validPass", "Password not valid"),
  };

  const months: { [key: string]: string } = {
    1: t("months.janShort", "Jan"),
    2: t("months.febShort", "Feb"),
    3: t("months.marShort", "Mar"),
    4: t("months.aprShort", "Apr"),
    5: t("months.mayShort", "May"),
    6: t("months.junShort", "Jun"),
    7: t("months.julShort", "Jul"),
    8: t("months.augShort", "Aug"),
    9: t("months.sepShort", "Sep"),
    10: t("months.octShort", "Oct"),
    11: t("months.novShort", "Nov"),
    12: t("months.decShort", "Dec"),
  };

  const [state, setState] = useState<IState>({
    birthDay:
      player && player.date_of_birth
        ? Number(player.date_of_birth.split("-")[2])
        : 0,
    birthMonth:
      player && player.date_of_birth
        ? Number(player.date_of_birth.split("-")[1])
        : 0,
    birthYear:
      player && player.date_of_birth
        ? Number(player.date_of_birth.split("-")[0])
        : 0,
    email:
      player && player.email.indexOf("@ismfgsocial") === -1 ? player.email : "",
    emailAllsvenskan: player ? player.email_allsvenskan : false,
    emailFavourite: player ? player.email_favourite : false,
    errorDob: "",
    errorEmail: "",
    errorFName: "",
    errorLName: "",
    errorPass: "",
    errorRegion: "",
    firstName: player ? player.first_name : "",
    lastName: player ? player.last_name : "",
    password: "",
    region: player ? player.region : 0,
  });

  const getMode = () => {
    if (!player) {
      return "register";
    }
    return player.dirty && !player.stitched ? "confirm" : "update";
  };

  const getRequiredFields = (): Array<keyof IState> => {
    switch (getMode()) {
      case "register":
        return [
          "birthDay",
          "birthMonth",
          "birthYear",
          "email",
          "firstName",
          "lastName",
          "password",
          "region",
        ];
      case "confirm":
      case "update":
        return [
          "birthDay",
          "birthMonth",
          "birthYear",
          "email",
          "firstName",
          "lastName",
          "region",
        ];
    }
  };

  const apiDataFromState = () => {
    switch (getMode()) {
      case "register":
        return {
          date_of_birth: `${state.birthYear}-${state.birthMonth}-${state.birthDay}`,
          email: state.email,
          email_allsvenskan: state.emailAllsvenskan,
          email_favourite: state.emailFavourite,
          first_name: state.firstName,
          last_name: state.lastName,
          password: state.password,
          region: state.region,
        };
      case "confirm":
      case "update":
        return {
          date_of_birth: `${state.birthYear}-${state.birthMonth}-${state.birthDay}`,
          email: state.email,
          email_allsvenskan: state.emailAllsvenskan,
          email_favourite: state.emailFavourite,
          first_name: state.firstName,
          last_name: state.lastName,
          region: state.region,
        };
    }
  };

  const handleBirthDayChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, birthDay: parseInt(e.currentTarget.value, 10) });

  const handleBirthMonthChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, birthMonth: parseInt(e.currentTarget.value, 10) });

  const handleBirthYearChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, birthYear: parseInt(e.currentTarget.value, 10) });

  const handleemailAllsvenskanChange = (
    e: React.SyntheticEvent<HTMLInputElement>,
  ) =>
    setState({
      ...state,
      emailAllsvenskan: e.currentTarget.checked ? true : false,
    });

  const handleEmailFavouriteChange = (
    e: React.SyntheticEvent<HTMLInputElement>,
  ) =>
    setState({
      ...state,
      emailFavourite: e.currentTarget.checked ? true : false,
    });

  const handleFirstNameChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, firstName: e.currentTarget.value });

  const handleLastNameChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, lastName: e.currentTarget.value });

  const handlePasswordChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, password: e.currentTarget.value });

  const handleRegionChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, region: parseInt(e.currentTarget.value, 10) });

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    const mode = getMode();
    if (mode === "register") {
      try {
        await dispatch(register(apiDataFromState()));
      } catch (e) {
        window.scrollTo(0, 0); // To see error if failure ...
        return;
      }
      await doBootstrap();
      navigate("/squad-selection");
    } else if (mode === "confirm") {
      try {
        await updatePlayer(apiDataFromState());
      } catch (e) {
        window.scrollTo(0, 0); // To see error if failure ...
        return;
      }
      await doBootstrap();
      navigate("/squad-selection");
    } else if (mode === "update") {
      try {
        await updatePlayer(apiDataFromState());
      } catch (e) {
        window.scrollTo(0, 0); // To see error if failure ...
        return;
      }
      await doBootstrap();
      // Maybe this should be back to my team, maybe our LoggedIn component
      navigate("/");
    }
  };

  useEffect(() => {
    dispatch(fetchRegions());
  }, []);

  const getIntRegions = () => {
    const regs = regions;
    return sortBy(regs, ["name"]);
  };

  const handleFirstnameBlur = (e: React.SyntheticEvent<HTMLInputElement>) => {
    if (!e.currentTarget.value) {
      setState({ ...state, errorFName: errorsTxt.EFName });
    } else {
      setState({ ...state, errorFName: "" });
    }
  };

  const handleLastnameBlur = (e: React.SyntheticEvent<HTMLInputElement>) => {
    if (!e.currentTarget.value) {
      setState({ ...state, errorLName: errorsTxt.ELName });
    } else {
      setState({ ...state, errorLName: "" });
    }
  };

  const handlePasswordBlur = (e: React.SyntheticEvent<HTMLInputElement>) => {
    if (!e.currentTarget.value) {
      setState({ ...state, errorPass: errorsTxt.EPass });
    } else {
      if (e.currentTarget.value.length < 6) {
        setState({ ...state, errorPass: errorsTxt.EPassLenght });
      } else {
        setState({ ...state, errorPass: "" });
      }
    }
  };

  const handleRegionBlur = (e: React.SyntheticEvent<HTMLSelectElement>) => {
    if (!e.currentTarget.value || e.currentTarget.value === "0") {
      setState({ ...state, errorRegion: errorsTxt.ERegion });
    } else {
      setState({ ...state, errorRegion: "" });
    }
  };

  const handleDobBlur = (_: React.SyntheticEvent<HTMLSelectElement>) => {
    const year = state.birthYear;
    const month = state.birthMonth;
    const day = state.birthDay;
    if (year > 0 && month > 0 && day > 0) {
      if (moment(new Date(year, month - 1, day)).isValid()) {
        const today = new Date();
        const birthDate = new Date(year, month - 1, day);
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        if (age < 13) {
          setState({ ...state, errorDob: errorsTxt.EDob });
        } else {
          setState({ ...state, errorDob: "" });
        }
      } else {
        setState({ ...state, errorDob: errorsTxt.EDob });
      }
    }
  };

  const handleServerErrors = () => {
    const errors = getMode() === "register" ? registerError : updateError;
    const formattedErrors = [];
    if (errors) {
      if (
        errors.badRequest &&
        errors.badRequest.password &&
        errors.badRequest.password[0].code === "invalid"
      ) {
        formattedErrors.push(errorsTxt.EValidPass);
      }
      if (
        errors.badRequest &&
        errors.badRequest.email &&
        errors.badRequest.email[0].code === "unique"
      ) {
        formattedErrors.push(errorsTxt.EvalidEmail);
      }
      if (
        errors.badRequest &&
        errors.badRequest.email &&
        errors.badRequest.email[0].code === "blank"
      ) {
        formattedErrors.push(errorsTxt.EEmail);
      }
      if (
        errors.badRequest &&
        errors.badRequest.password &&
        errors.badRequest.password[0].code === "blank"
      ) {
        formattedErrors.push(errorsTxt.EPass);
      }
      if (
        errors.badRequest &&
        errors.badRequest.last_name &&
        errors.badRequest.last_name[0].code === "blank"
      ) {
        formattedErrors.push(errorsTxt.EFName);
      }
      if (
        errors.badRequest &&
        errors.badRequest.last_name &&
        errors.badRequest.last_name[0].code === "blank"
      ) {
        formattedErrors.push(errorsTxt.ELName);
      }
      if (
        errors.badRequest &&
        errors.badRequest.date_of_birth &&
        errors.badRequest.date_of_birth[0].code === "invalid"
      ) {
        formattedErrors.push(errorsTxt.EValidDob);
      }
      if (
        errors.badRequest &&
        errors.badRequest.region &&
        errors.badRequest.region[0].code === "does_not_exist"
      ) {
        formattedErrors.push(errorsTxt.ERegion);
      }

      return formattedErrors;
    }
  };

  const mode = getMode();

  const title = {
    register: t("register.title.registration", "Registration"),
    confirm: t("register.title.confirm", "Confirm your details"),
    update: t("register.title.update", "Confirm your details"),
  };
  const buttonText = {
    register: t("register.label.register", "Register"),
    confirm: t("register.label.confirm", "Update"),
    update: t("register.label.update", "Update"),
  };
  const disabled = getRequiredFields().some((e) => !state[e]);
  const errors = handleServerErrors();

  return (
    <Wrapper>
      <Main>
        <Box mx={2}>
          <Title>{title[mode]}</Title>
          <p>
            {t(
              "register.required",
              "Fields marked with an asterisk (*) are required",
            )}
            .
          </p>
        </Box>

        {errors && <Alert type="error">{values(errors).map((r) => r)}</Alert>}
        <Copy>
          <form onSubmit={handleSubmit}>
            {mode === "register" && (
              <Box mt={2}>
                <InputField
                  id="ismRegisterPassword"
                  type="password"
                  hasErrors={!!state.errorPass}
                  label={t("register.label.password", "Password")}
                  value={state.password}
                  onChange={handlePasswordChange}
                  hint={t("register.hint.passsword", "Minimum 6 characters")}
                  onBlur={handlePasswordBlur}
                />
                {state.errorPass && (
                  <FieldFeedback>{state.errorPass}</FieldFeedback>
                )}
              </Box>
            )}
            <Box mt={2}>
              <InputField
                id="ismRegisterFirstName"
                hasErrors={!!state.errorFName}
                label={t("register.label.fName", "First Name")}
                value={state.firstName}
                onChange={handleFirstNameChange}
                onBlur={handleFirstnameBlur}
              />
              {state.errorFName && (
                <FieldFeedback>{state.errorFName}</FieldFeedback>
              )}
            </Box>
            <Box mt={2}>
              <InputField
                id="ismRegisterLastName"
                label={t("register.label.lName", "Last Name")}
                hasErrors={!!state.errorLName}
                value={state.lastName}
                onChange={handleLastNameChange}
                onBlur={handleLastnameBlur}
              />
              {state.errorLName && (
                <FieldFeedback>{state.errorLName}</FieldFeedback>
              )}
            </Box>
            <Box mt={2}>
              <DOBFieldset>
                <DOBLegend>
                  {t("register.label.dob", "Date of birth")}
                </DOBLegend>
                <span>
                  {t(
                    "register.hint.dob",
                    "The game is only available to individuals aged 13 and over",
                  )}
                </span>
                <DOBFieldsetInner>
                  <DOBCol>
                    <SelectField
                      labelVisbility={false}
                      id="ismRegisterBirthDay"
                      value={state.birthDay}
                      onChange={handleBirthDayChange}
                      label={t("register.label.day", "Day")}
                      onBlur={handleDobBlur}
                    >
                      <option
                        value="0"
                        aria-selected={!state.birthDay}
                        disabled
                        defaultValue="0"
                      >
                        {t("register.label.day", "Day")}
                      </option>
                      {range(1, 32).map((d) => (
                        <option
                          value={d}
                          aria-selected={d === state.birthDay}
                          key={d}
                        >
                          {d}
                        </option>
                      ))}
                    </SelectField>
                  </DOBCol>
                  <DOBCol>
                    <SelectField
                      labelVisbility={false}
                      id="ismRegisterBirthMonth"
                      value={state.birthMonth}
                      onChange={handleBirthMonthChange}
                      label={t("register.label.month", "Month")}
                      onBlur={handleDobBlur}
                    >
                      <option
                        value="0"
                        aria-selected={!state.birthMonth}
                        disabled
                        defaultValue="0"
                      >
                        {t("register.label.month", "Month")}
                      </option>
                      {Object.keys(months).map((m) => (
                        <option
                          value={m}
                          aria-selected={parseInt(m, 10) === state.birthMonth}
                          key={m}
                        >
                          {months[m]}
                        </option>
                      ))}
                    </SelectField>
                  </DOBCol>
                  <DOBCol>
                    <SelectField
                      labelVisbility={false}
                      value={state.birthYear}
                      onChange={handleBirthYearChange}
                      label={t("register.label.year", "Year")}
                      id="ismRegisterBirthYear"
                      onBlur={handleDobBlur}
                    >
                      <option
                        value="0"
                        aria-selected={!state.birthYear}
                        disabled
                        defaultValue="0"
                      >
                        {t("register.label.year", "Year")}
                      </option>
                      {range(new Date().getFullYear() - 13, 1900).map((y) => (
                        <option
                          value={y}
                          aria-selected={y === state.birthYear}
                          key={y}
                        >
                          {y}
                        </option>
                      ))}
                    </SelectField>
                  </DOBCol>
                </DOBFieldsetInner>
                {state.errorDob && (
                  <FieldFeedback>{state.errorDob}</FieldFeedback>
                )}
              </DOBFieldset>
            </Box>
            <Box mt={2}>
              {state.errorRegion && (
                <FieldFeedback>{state.errorRegion}</FieldFeedback>
              )}
              <SelectField
                value={state.region}
                onChange={handleRegionChange}
                label={t("register.label.regionn", "Region")}
                id="ismRegisterRegion"
                onBlur={handleRegionBlur}
              >
                <option value="0" aria-selected={!state.region}>
                  {t("register.label.select", "Select")}
                </option>
                <optgroup
                  label={t<string>(
                    "register.label.international",
                    "International",
                  )}
                >
                  {values(getIntRegions()).map((r) => (
                    <option
                      value={r.id}
                      aria-selected={r.id === state.region}
                      key={r.id}
                    >
                      {r.name}
                    </option>
                  ))}
                </optgroup>
              </SelectField>
            </Box>
            <Box mt={2}>
              <p>
                {t(
                  "register.emailText",
                  "Receive information and newsletter from",
                )}
              </p>
              <CheckboxField
                checked={state.emailFavourite}
                id="ismRegisterEmailFavourite"
                label={t("register.label.emailClub", "Your favourite club")}
                onChange={handleEmailFavouriteChange}
              />
              <CheckboxField
                checked={state.emailAllsvenskan}
                id="ismRegisteremailAllsvenskan"
                label={t("register.label.emailClient", "Allsvenskan")}
                onChange={handleemailAllsvenskanChange}
              />
            </Box>
            <Box mt={2}>
              {mode === "confirm" && (
                <Trans i18nKey="register.terms1">
                  By updating your account details, you confirm that you accept
                  the&nbsp;
                  <a
                    href="/help/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    User Terms
                  </a>
                  &nbsp;and&nbsp;
                  <a
                    href={t<string>(
                      "register.integrityPolicy",
                      "https://www.svenskelitfotboll.se/privacy-policy-2/",
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    SEF's Integrity Policy
                  </a>
                  .&nbsp;
                </Trans>
              )}
              {mode === "update" && (
                <Trans i18nKey="register.terms1">
                  By updating your account details, you confirm that you accept
                  the&nbsp;
                  <a
                    href="/help/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    User Terms
                  </a>
                  &nbsp;and&nbsp;
                  <a
                    href={t<string>(
                      "register.integrityPolicy",
                      "https://www.svenskelitfotboll.se/privacy-policy-2/",
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    SEF's Integrity Policy
                  </a>
                  .&nbsp;
                </Trans>
              )}
              {mode === "register" && (
                <Trans i18nKey="register.terms3">
                  By creating an account, you confirm that you accept the &nbsp;
                  <a
                    href="/help/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    User Terms
                  </a>
                  &nbsp;and&nbsp;
                  <a
                    href={t<string>(
                      "register.integrityPolicy",
                      "https://www.svenskelitfotboll.se/privacy-policy-2/",
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    SEF's Integrity Policy
                  </a>
                  .&nbsp;
                </Trans>
              )}
              <Trans i18nKey="register.terms2">
                If you have opted to receive information from your favorite
                team, you also confirm that you have read your&nbsp;
                <a
                  href="https://www.svenskelitfotboll.se/integritetspolicy-klubbar/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  favorite club's Integrity Policy
                </a>
                .
              </Trans>
            </Box>
            <Box mt={2} mb={8}>
              <Button type="submit" disabled={disabled}>
                {buttonText[mode]}
              </Button>
            </Box>
          </form>
        </Copy>
      </Main>
    </Wrapper>
  );
};

export { Register as RegisterTest };

export default withTranslation()(Register);
